<script setup>
import { ref, nextTick, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Trash2, Pencil, Check } from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  isActive: {
    type: Boolean,
    default: false
  },
  timezone: {
    type: String,
    default: 'UTC'
  }
})

const emit = defineEmits(['select', 'delete', 'update'])

// Edit state
const isEditing = ref(false)
const editingName = ref('')
const editInputRef = ref(null)

const handleClick = () => {
  if (isEditing.value) return
  emit('select', props.session)
}

const handleDeleteClick = (event) => {
  event.stopPropagation()
  emit('delete', props.session)
}

const startEditing = async (event) => {
  event.stopPropagation()
  isEditing.value = true
  editingName.value = props.session.name || ''
  await nextTick()
  const inputEl = editInputRef.value?.$el || editInputRef.value
  inputEl?.focus()
}

const saveEdit = (event) => {
  event?.stopPropagation()
  const trimmedName = editingName.value.trim()

  if (!trimmedName) {
    toast({
      title: 'Invalid name',
      description: 'Chat name cannot be empty',
      variant: 'destructive'
    })
    // Revert to original name
    editingName.value = props.session.name || ''
    return
  }

  emit('update', {
    session_code: props.session.session_code,
    name: trimmedName
  })
  cancelEdit()
}

const cancelEdit = () => {
  isEditing.value = false
  editingName.value = ''
}

const handleKeydown = (event) => {
  if (event.key === 'Enter') {
    saveEdit(event)
  } else if (event.key === 'Escape') {
    event.stopPropagation()
    cancelEdit()
  }
}
</script>

<template>
  <div
    @click="handleClick"
    class="p-3 cursor-pointer hover:bg-muted transition-colors group"
    :class="{
      'bg-primary/10 border-l-2 border-l-primary': isActive,
      'border-l-2 border-l-transparent': !isActive
    }"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex-1 min-w-0">
        <!-- Editing mode -->
        <div v-if="isEditing" class="flex items-center gap-1">
          <Input
            ref="editInputRef"
            v-model="editingName"
            @keydown="handleKeydown"
            @click.stop
            class="h-7 text-sm"
            placeholder="Chat name"
            aria-label="Edit chat name"
          />
          <Button
            @click="saveEdit"
            variant="ghost"
            size="sm"
            class="h-7 w-7 p-0 text-green-600 hover:text-green-700 flex-shrink-0"
            aria-label="Save chat name"
          >
            <Check class="h-4 w-4" />
          </Button>
        </div>
        <!-- Display mode -->
        <template v-else>
          <h3 class="text-sm font-medium truncate">
            {{ session.name || 'Untitled Chat' }}
          </h3>
          <p class="text-xs text-muted-foreground mt-0.5">
            {{ formatTime(session.created_at, timezone, 'MMM D, HH:mm') }}
          </p>
        </template>
      </div>
      <!-- Action buttons (only show when not editing) -->
      <div v-if="!isEditing" class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
        <Button
          @click="startEditing"
          variant="ghost"
          size="sm"
          class="h-7 w-7 p-0 text-muted-foreground hover:text-blue-600"
          aria-label="Edit chat name"
        >
          <Pencil class="h-4 w-4" />
        </Button>
        <Button
          @click="handleDeleteClick"
          variant="ghost"
          size="sm"
          class="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
          aria-label="Delete chat"
        >
          <Trash2 class="h-4 w-4" />
        </Button>
      </div>
    </div>
  </div>
</template>
